import { useMemo } from 'react';
import { Namespace, SecretVariable, SecretFile, Keyvaults, KeyvaultKey } from '@app/SecretManager/SecretsExplorer/model/repository';
import { SecretDetailEvent } from './../../../../../shared/secretmanager/secretsexplorer/types';
import { useSecretDetail } from '../../hooks/useSecretManagerData';

const useSecretDetails = (
  namespaces: Namespace[],
  keyvaults: Keyvaults,
  namespaceName: string,
  applicationName: string,
  containerName: string,
  itemName: string,
  isFile: boolean,
) => {
  const currentItem = useMemo(() => {
    const currentNamespace = namespaces.find(ns => ns.name === namespaceName);
    const currentApplication = currentNamespace?.applications.find(app => app.name === applicationName);
    const currentContainer = currentApplication?.containers?.find(cont => cont.name === containerName);

    if (isFile) {
      return currentContainer?.secret_files.find(fileObj => fileObj.name === itemName);
    }
    return currentContainer?.secret_variables.find(varObj => varObj.name === itemName);
  }, [namespaces, namespaceName, applicationName, containerName, itemName, isFile]);

  const vault = currentItem?.keyvault_key_ref.keyvault_name || '';
  const name = currentItem?.keyvault_key_ref.keyvault_key || '';

  const detailQuery = useSecretDetail(vault, name, !!currentItem);

  const usedBy = useMemo(() => {
    if (!currentItem) return [];

    const keyvault = keyvaults[currentItem.keyvault_key_ref.keyvault_name];
    if (!keyvault) return [];

    const keyvault_key = keyvault.keys[currentItem.keyvault_key_ref.keyvault_key];
    if (!keyvault_key || typeof keyvault_key === 'string') return [];

    if (!keyvault_key.references) return [];

    return keyvault_key.references.filter(
      ref =>
        !(
          ref.namespace === namespaceName &&
          ref.application.name === applicationName &&
          ref.container.name === containerName &&
          ref.secret.name === itemName
        ),
    );
  }, [keyvaults, currentItem, namespaceName, applicationName, containerName, itemName]);

  const lookupError = useMemo(() => {
    if (!currentItem) return new Error(`${isFile ? 'File' : 'Secret'} ${itemName} not found.`);
    return null;
  }, [currentItem, isFile, itemName]);

  const history: SecretDetailEvent[] = detailQuery.data?.history ?? [];
  const canReveal = detailQuery.data?.actions[0]?.enabled ?? false;

  return {
    item: currentItem as SecretVariable | SecretFile | undefined,
    usedBy,
    history,
    canReveal,
    isLoading: detailQuery.isLoading,
    error: lookupError || (detailQuery.error ? (detailQuery.error as Error) : null),
  };
};

export default useSecretDetails;
